import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
} from "components";
import { CalculatorIcon, TagIcon } from "@heroicons/react/24/outline";
import { useAppNavigate, useAppSearch } from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import CreateTargetContainer from "./create";
import { FC } from "react";
import RemoveTargetContainer from "./remove";
import Table from "components/tables/table";
import { FetchTargets_fetchTargets as Target } from "_graphql/queries/__generated__/fetchTargets";
import UpdateTargetContainer from "./update";
import ViewTargetContainer from "./view";
import { usePagination } from "hooks";
import { useTargets } from "hooks/data/use-targets";

type TargetsPageProps = {};

const TargetsPage: FC<TargetsPageProps> = () => {
  const navigate = useAppNavigate();
  const searchParams = useAppSearch();
  const { offset, limit, setPage } = usePagination();

  const { targets, loading, refetch } = useTargets({
    brokerId: "1",
  });

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
      () => {
        navigate({
          search: (old) => ({
            ...old,
            id,
            modal: action,
          }),
        });
      };

  const navigateToBadges = (id: string) => () => {
    navigate({
      to: `./${id}/target-badges`,
    });
  };

  const navigateToRates = (id: string) => () => {
    navigate({
      to: `./${id}/target-rates`,
    });
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <OfficeButton
            onClick={() =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    modal: "create",
                  };
                },
              })
            }
          >
            Create Target
          </OfficeButton>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <Table
            title="Targets"
            loading={loading}
            hasSearch
            columns={[
              {
                name: "Target Name",
                accessor: "target_name",
                render: (item: Target) => (
                  <div className="flex items-center space-x-2">
                    <TagIcon className="h-6 w-6" />
                    <div className="flex-1">{item.target_name}</div>
                  </div>
                ),
              },
              {
                name: "Currency",
                accessor: "base_currency",
                render: (item: Target) => (
                  <div className="flex items-center">
                    <div className="flex-1">{item.base_currency || "GHS"}</div>
                  </div>
                ),
              },
              {
                name: "Date Range",
                accessor: "start_date",
                render: (item: Target) => (
                  <div className="flex items-center">
                    <div className="flex-1">
                      {item.start_date} - {item.end_date}
                    </div>
                  </div>
                ),
              },
              {
                name: "Badge Count",
                accessor: "target_badges",
                render: (item: Target) => (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      {item.target_badges?.length || 0} badges
                    </div>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "id",
                render: (item: Target) => (
                  <>
                    <ActionButton
                      action="expand"
                      tooltip="View Badges"
                      onClick={navigateToBadges(item?.id || "")}
                    />
                    <ActionButton
                      action="calculate"
                      tooltip="View Rates"
                      onClick={navigateToRates(item?.id || "")}
                    />
                    <ActionButton
                      action="view"
                      onClick={dispatchAction(item?.id || "", "view")}
                    />
                    <ActionButton
                      action="update"
                      onClick={dispatchAction(item?.id || "", "update")}
                    />
                    <ActionButton
                      action="remove"
                      extraClassName="text-red-400"
                      onClick={dispatchAction(item?.id || "", "remove")}
                    />
                  </>
                ),
              },
            ]}
            data={targets}
            refetch={refetch}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={targets.length}
                currentSize={targets.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>

      <CreateTargetContainer
        open={searchParams?.modal === "create"}
        setOpen={(value) => {
          navigate({
            search(prev) {
              return {
                ...prev,
                modal: value ? "create" : undefined,
              };
            },
          });
        }}
        refetch={refetch}
        brokerId="1"
      />

      {searchParams?.id && (
        <>
          <ViewTargetContainer
            open={searchParams?.modal === "view"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "view" : undefined,
                  };
                },
              });
            }}
          />
          <UpdateTargetContainer
            open={searchParams?.modal === "update"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "update" : undefined,
                  };
                },
              });
            }}
            refetch={refetch}
          />
          <RemoveTargetContainer
            open={searchParams?.modal === "remove"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "remove" : undefined,
                  };
                },
              });
            }}
          />
        </>
      )}
    </main>
  );
};

export default TargetsPage;
